import { useCallback, useState } from "react";
import * as Sentry from "@sentry/react";

import { RosterManagementPanel } from "@/components/dashboard/CampaignDetailViews";
import {
  MonolithBoardModal,
  MONOLITH_BOARD_MODAL_TITLE_CLASS,
} from "@/components/dashboard/MonolithBoardModal";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/ToastProvider";
import {
  useRemoveMemberMutation,
  useUpdateMemberRoleMutation,
} from "@/lib/queries/campaignQueries";
import type { CampaignMemberRole, UserCampaignRecord } from "@west-marches/shared";

interface MembersModalProps {
  open: boolean;
  onClose: () => void;
  campaign: UserCampaignRecord | null;
  canManageMembers: boolean;
  onRosterUpdate: (roster: UserCampaignRecord["roster"]) => void;
}

function reportMembersError(action: string, error: unknown, extras?: Record<string, unknown>) {
  Sentry.captureException(error instanceof Error ? error : new Error(action), {
    tags: { area: "members-modal", action },
    extra: extras,
  });
}

export default function MembersModal({
  open,
  onClose,
  campaign,
  canManageMembers,
  onRosterUpdate,
}: MembersModalProps) {
  const toast = useToast();
  const updateMemberRoleMutation = useUpdateMemberRoleMutation();
  const removeMemberMutation = useRemoveMemberMutation();
  const [updatingRole, setUpdatingRole] = useState(false);
  const [removingMember, setRemovingMember] = useState(false);

  const handleUpdateRole = useCallback(
    async (userId: string, role: CampaignMemberRole) => {
      if (!campaign || !canManageMembers) {
        return;
      }

      setUpdatingRole(true);
      try {
        await updateMemberRoleMutation.mutateAsync({
          campaignId: campaign.id,
          userId,
          role,
        });
        onRosterUpdate(
          campaign.roster.map((member) =>
            member.userId === userId ? { ...member, kind: role } : member,
          ),
        );
        toast.success(`Role updated to ${role.toUpperCase()}.`);
      } catch (error) {
        reportMembersError("update-member-role", error, {
          campaignId: campaign.id,
          userId,
          role,
        });
        toast.error(error instanceof Error ? error.message : "Failed to update role.");
      } finally {
        setUpdatingRole(false);
      }
    },
    [campaign, canManageMembers, onRosterUpdate, toast, updateMemberRoleMutation],
  );

  const handleRemoveMember = useCallback(
    async (userId: string) => {
      if (!campaign || !canManageMembers) {
        return;
      }

      setRemovingMember(true);
      try {
        await removeMemberMutation.mutateAsync({ campaignId: campaign.id, userId });
        onRosterUpdate(campaign.roster.filter((member) => member.userId !== userId));
        toast.success("Member removed.");
      } catch (error) {
        reportMembersError("remove-member", error, {
          campaignId: campaign.id,
          userId,
        });
        toast.error(error instanceof Error ? error.message : "Failed to remove member.");
      } finally {
        setRemovingMember(false);
      }
    },
    [campaign, canManageMembers, onRosterUpdate, removeMemberMutation, toast],
  );

  const submitting = updatingRole || removingMember;

  return (
    <MonolithBoardModal
      open={open && campaign !== null}
      titleId="members-modal-title"
      onClose={onClose}
      submitting={submitting}
      panelClassName="max-w-2xl"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 id="members-modal-title" className={MONOLITH_BOARD_MODAL_TITLE_CLASS}>
            Members
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-neutral-700">
            {canManageMembers
              ? "Review the current roster and manage campaign roles from one place."
              : "Review the current roster for this campaign."}
          </p>
        </div>
        <Button
          type="button"
          variant="cancel"
          chrome="compact"
          className="px-3 py-1.5 text-[10px]"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close members dialog"
        >
          Close
        </Button>
      </div>

      <div className="mt-6">
        {campaign ? (
          <RosterManagementPanel
            campaign={campaign}
            canManageMembers={canManageMembers}
            onUpdateRole={handleUpdateRole}
            updatingRole={updatingRole}
            onRemoveMember={handleRemoveMember}
            removingMember={removingMember}
          />
        ) : null}
      </div>
    </MonolithBoardModal>
  );
}
